import { brandPalette } from "../../constants/brandPalette";
/** Shared MainModel + react-select styling for admin forms (matches Add student).
 *
 * Dark-mode-aware via CSS variables defined in
 * `src/styles/brand-variables.css`. Both Chakra style props (`bg`,
 * `borderColor`) and react-select inline styles consume the same
 * `var(--surface-raised)` / `var(--text-primary)` / `var(--border-subtle)`
 * strings, which flip automatically when `<html data-theme="dark">` is set
 * by the AdminThemeProvider. We do not use Chakra `semanticTokens` /
 * `useColorMode` here because this project pins @chakra-ui/react@1.8.x,
 * which predates those APIs.
 */

export const adminModalContentProps = {
  dir: "ltr",
  bg: "var(--surface-raised)",
  borderRadius: "16px",
  overflow: "hidden",
  boxShadow: "2xl",
};

export const adminModalHeaderProps = {
  px: 8,
  pt: 8,
  pb: 6,
  borderBottom: "1px solid",
  borderColor: "var(--border-subtle)",
};

export const adminModalBodyProps = { px: 8, py: 8 };

/**
 * @param {{ hasError?: boolean }} [opts]
 */
export const adminSelectStyles = (opts = {}) => {
  const { hasError } = opts;
  return {
    control: (styles, state) => ({
      ...styles,
      width: "100%",
      backgroundColor: "var(--surface-raised)",
      color: "var(--text-primary)",
      borderRadius: "12px",
      borderColor: hasError
        ? "#E53E3E"
        : state.isFocused
          ? brandPalette.primary
          : "var(--border-subtle)",
      borderWidth: "1px",
      boxShadow: state.isFocused ? "0 0 0 1px rgba(91, 13, 22, 0.22)" : "none",
      minHeight: "42px",
      ":hover": {
        borderColor: hasError ? "#E53E3E" : brandPalette.primary,
      },
    }),
    input: (styles) => ({
      ...styles,
      color: "var(--text-primary)",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    placeholder: (styles) => ({
      ...styles,
      color: "var(--text-muted)",
      fontWeight: 600,
      fontSize: "14px",
    }),
    singleValue: (styles) => ({
      ...styles,
      color: "var(--text-primary)",
      fontWeight: 600,
      fontSize: "14px",
    }),
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: "rgba(91, 13, 22, 0.18)",
      borderRadius: "8px",
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: brandPalette.secondary,
      fontWeight: 600,
      fontSize: "13px",
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: brandPalette.primary,
      ":hover": { backgroundColor: "rgba(91, 13, 22, 0.28)", color: brandPalette.secondary },
    }),
    menu: (styles) => ({
      ...styles,
      borderRadius: "12px",
      overflow: "hidden",
      border: "1px solid var(--border-subtle)",
      backgroundColor: "var(--surface-raised)",
      boxShadow: "0 16px 40px rgba(0, 0, 0, 0.35)",
      zIndex: 50,
    }),
    menuList: (styles) => ({
      ...styles,
      backgroundColor: "var(--surface-raised)",
    }),
    option: (styles, state) => ({
      ...styles,
      fontSize: "14px",
      fontWeight: state.isSelected ? 800 : 600,
      color: state.isSelected ? brandPalette.cream : "var(--text-primary)",
      backgroundColor: state.isSelected
        ? brandPalette.secondary
        : state.isFocused
          ? "rgba(91, 13, 22, 0.12)"
          : "transparent",
      ":active": {
        backgroundColor: "rgba(91, 13, 22, 0.22)",
      },
    }),
  };
};

/**
 * Pill-shaped react-select — group filter style (pill control; menu options use off-white selected state).
 * @param {{ menuZIndex?: number }} [opts] — raise inside modals so the menu appears above the overlay (e.g. 50).
 */
export const adminPillSelectStyles = (opts = {}) => {
  const { menuZIndex = 20 } = opts;
  return {
    control: (styles, state) => ({
      ...styles,
      width: "100%",
      minHeight: "40px",
      backgroundColor: "var(--surface-raised)",
      borderColor: state.isFocused ? brandPalette.primary : "var(--border-subtle)",
      borderWidth: "1px",
      borderRadius: "9999px",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(91, 13, 22, 0.12)" : "none",
      cursor: "pointer",
      ":hover": {
        borderColor: brandPalette.primary,
      },
    }),
    input: (styles) => ({
      ...styles,
      color: "var(--text-primary)",
    }),
    valueContainer: (styles) => ({
      ...styles,
      paddingLeft: "12px",
      paddingRight: "8px",
    }),
    placeholder: (styles) => ({
      ...styles,
      color: "var(--text-muted)",
      fontWeight: 600,
      fontSize: "14px",
    }),
    singleValue: (styles) => ({
      ...styles,
      color: brandPalette.secondary,
      fontWeight: 700,
      fontSize: "14px",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (styles, state) => ({
      ...styles,
      color: state.isFocused ? brandPalette.primary : "rgba(91, 13, 22, 0.7)",
      ":hover": { color: brandPalette.primary },
    }),
    menu: (styles) => ({
      ...styles,
      borderRadius: "16px",
      overflow: "hidden",
      border: "1px solid var(--border-subtle)",
      backgroundColor: "var(--surface-raised)",
      boxShadow: "0 16px 40px rgba(0, 0, 0, 0.35)",
      zIndex: menuZIndex,
    }),
    menuList: (styles) => ({
      ...styles,
      padding: "6px",
      backgroundColor: "var(--surface-raised)",
    }),
    option: (styles, state) => {
      const active = state.isSelected || state.isFocused;
      return {
        ...styles,
        borderRadius: "12px",
        padding: "10px 12px",
        fontSize: "14px",
        fontWeight: state.isSelected ? 800 : 600,
        color: state.isSelected ? brandPalette.cream : "var(--text-primary)",
        backgroundColor: state.isSelected
          ? brandPalette.secondary
          : active
            ? "rgba(91, 13, 22, 0.12)"
            : "transparent",
        cursor: "pointer",
        ":active": {
          backgroundColor: "rgba(91, 13, 22, 0.22)",
        },
      };
    },
  };
};
